"use client";

import React from "react";
import { Activity, Sparkles, ArrowRight, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const philosophyBlocks = [{
  title: "Restoring Stability and Dignity",
  description: "The Trust Foundation is dedicated to providing a firm foundation for those navigating life's most difficult transitions. Through comprehensive case management and individual support planning, we help our community members reclaim their sense of self-worth and establish a secure path toward long-term independence.",
  quote: "Dignity is not a destination, but the very ground upon which we build a new life.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654300/img/community_support_mentorship.png",
  imageAlt: "A supportive group meeting reflecting community and belonging",
  icon: Activity,
  reverse: false
}, {
  title: "Pathways to Lasting Opportunity",
  description: "Our commitment extends beyond immediate relief to sustainable growth. By integrating life skills development, mentorship, and community resource navigation, we empower individuals with the practical tools and emotional resilience needed to thrive in their personal and professional journeys.",
  quote: "Transformation happens when we replace uncertainty with the skills to lead.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1654300/img/mentorship_empowerment_session.png",
  imageAlt: "A mentor and mentee engaging in a personal development session",
  icon: Sparkles,
  reverse: true
}];
function PhilosophyBlock({
  block,
  index
}: {
  block: (typeof philosophyBlocks)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const Icon = block.icon;
  return <div data-index={index} ref={ref} className={`flex flex-col gap-12 lg:gap-24 items-center transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"} ${block.reverse ? "lg:flex-row-reverse" : "lg:flex-row"}`}>
      {}
      <div className="w-full lg:w-1/2 relative group">
        {}
        <div className="absolute -inset-4 bg-secondary/20 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
        
        <div className="relative z-10 p-2">
          <img src={block.image} alt={block.imageAlt} className={`w-full h-[400px] md:h-[500px] lg:h-[600px] object-cover shadow-2xl transition-transform duration-700 group-hover:scale-[1.02] ${block.reverse ? "rounded-tr-[120px] rounded-bl-[120px] rounded-tl-3xl rounded-br-3xl" : "rounded-tl-[120px] rounded-br-[120px] rounded-tr-3xl rounded-bl-3xl"}`} />
          {}
          <div className={`absolute ${block.reverse ? '-bottom-6 -left-6' : '-bottom-6 -right-6'} bg-background p-4 rounded-full shadow-xl z-20`}>
            <div className="bg-secondary/10 p-4 rounded-full text-secondary">
              <Icon className="w-8 h-8" />
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="w-full lg:w-1/2 flex flex-col justify-center space-y-8 px-4 sm:px-0">
        <div className="space-y-4">
          <h3 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-primary tracking-tight">
            {block.title}
          </h3>
          <p className="text-lg md:text-xl leading-relaxed text-muted-foreground font-default">
            {block.description}
          </p>
        </div>

        {}
        <blockquote className="relative pl-8 py-2 border-l-4 border-chart-2 my-8">
          <Quote className="absolute -left-3 -top-3 w-8 h-8 text-chart-2/20 rotate-180" />
          <p className="text-2xl md:text-3xl font-serif font-medium text-chart-2 italic leading-snug">
            "{block.quote}"
          </p>
        </blockquote>
      </div>
    </div>;
}
export default function OurPlayPhilosophy() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="223759" id="our-commitment-to-transformation" className="bg-background text-foreground py-24 lg:py-32 overflow-hidden relative">
      {}
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-secondary/10 via-background to-background pointer-events-none"></div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {}
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-20 transition-all duration-1000 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-secondary font-semibold tracking-widest uppercase text-sm mb-3">
            Our Story
          </h2>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold text-primary mb-6">
            A Journey of Hope and Growth
          </h1>
          <p className="text-xl text-muted-foreground font-default">
            The Trust Foundation blends professional advocacy with community-centered care to ensure every individual has the opportunity to thrive and belong.
          </p>
        </div>

        {}
        <div className="space-y-32 lg:space-y-40">
          {philosophyBlocks.map((block, i) => <PhilosophyBlock key={block.title} block={block} index={i} />)}
        </div>

        {}
        <div className="mt-32 text-center">
          <Button asChild size="lg" className="rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-2xl transition-all bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="/OurStoryPage">
              Discover Our Story
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}